import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Download, ZoomIn, ZoomOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Notice } from "@/components/Notice";
import { ApiError } from "@/lib/apiClient";
import { saveBlob } from "@/utils/download";

export interface FileViewerRequest {
  title?: string;
  name?: string;
  load: () => Promise<{ blob: Blob; filename?: string | null }>;
  errorMessage?: string;
  onClose?: () => void;
}

type ViewFn = (req: FileViewerRequest) => void;

const FileViewerContext = createContext<ViewFn | null>(null);

export function useFileViewer(): ViewFn {
  const ctx = useContext(FileViewerContext);
  if (!ctx) throw new Error("useFileViewer must be used within <FileViewerProvider>.");
  return ctx;
}

const ZOOM_STEPS = [50, 75, 100, 150, 200, 300];
const MIN_ZOOM = ZOOM_STEPS[0];
const MAX_ZOOM = ZOOM_STEPS[ZOOM_STEPS.length - 1];

type LoadedFile = { blob: Blob; url: string; type: string; filename: string | null };

export function FileViewerProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState<(FileViewerRequest & { key: number }) | null>(null);
  const [file, setFile] = useState<LoadedFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(100);
  const keyRef = useRef(0);

  const view = useCallback<ViewFn>((req) => {
    keyRef.current += 1;
    setFile(null);
    setError(null);
    setZoom(100);
    setRequest({ ...req, key: keyRef.current });
    setOpen(true);
  }, []);

  useEffect(() => {
    if (!request) return;
    let cancelled = false;
    let url: string | null = null;
    request
      .load()
      .then(({ blob, filename }) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setFile({ blob, url, type: blob.type || "", filename: filename ?? null });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const fallback =
          request.errorMessage ?? "Couldn't open the file — it may no longer be available.";
        const message = e instanceof ApiError || e instanceof Error ? e.message : "";
        setError(message.trim() ? message : fallback);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [request]);

  function close() {
    const done = request?.onClose;
    setOpen(false);
    setRequest(null);
    setFile(null);
    setError(null);
    done?.();
  }

  const displayName = file?.filename || request?.name || null;
  const isImage = file ? file.type.startsWith("image/") : false;
  const isPdf = file ? file.type === "application/pdf" : false;

  function stepZoom(dir: 1 | -1) {
    setZoom((z) => {
      const next = dir === 1
        ? ZOOM_STEPS.find((s) => s > z) ?? MAX_ZOOM
        : [...ZOOM_STEPS].reverse().find((s) => s < z) ?? MIN_ZOOM;
      return next;
    });
  }

  function download() {
    if (!file) return;
    saveBlob(file.blob, displayName || "attachment");
  }

  return (
    <FileViewerContext.Provider value={view}>
      {children}
      <Dialog open={open} onOpenChange={(next) => { if (!next) close(); }}>
        <DialogContent
          aria-describedby={undefined}
          className="flex max-h-[90vh] w-[min(56rem,95vw)] max-w-none flex-col gap-0 overflow-hidden p-0"
        >
          <DialogHeader className="min-w-0 space-y-0.5 border-b px-5 py-4 pr-12">
            <DialogTitle className="text-base font-semibold text-slate-900">
              {request?.title ?? "View Attachment"}
            </DialogTitle>
            {displayName && (
              <p className="truncate text-xs text-muted-foreground">{displayName}</p>
            )}
          </DialogHeader>

          <div className="min-h-[16rem] flex-1 overflow-auto bg-slate-50 p-5">
            {error ? (
              <Notice tone="error">{error}</Notice>
            ) : !file ? (
              <p className="py-16 text-center text-sm text-slate-500">Loading…</p>
            ) : isImage ? (
              <div className="flex min-h-full items-start justify-center">
                <img
                  src={file.url}
                  alt={displayName ?? "Attachment"}
                  style={{ maxWidth: `${zoom}%` }}
                  className="h-auto w-auto rounded-md bg-white shadow-sm"
                />
              </div>
            ) : isPdf ? (
              <iframe
                src={file.url}
                title={displayName ?? "Attachment"}
                className="h-[70vh] w-full rounded-md border bg-white"
              />
            ) : (
              <div className="py-16 text-center text-sm text-slate-500">
                <p>This file type can't be previewed in the browser.</p>
                <p className="mt-1">Download it to open with another app.</p>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t px-5 py-3">
            <div className="flex items-center gap-1">
              {isImage && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => stepZoom(-1)}
                    disabled={zoom <= MIN_ZOOM}
                    title="Zoom out"
                  >
                    <ZoomOut className="h-4 w-4" />
                    <span className="sr-only">Zoom out</span>
                  </Button>
                  <span className="w-14 text-center text-xs tabular-nums text-slate-600">{zoom}%</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => stepZoom(1)}
                    disabled={zoom >= MAX_ZOOM}
                    title="Zoom in"
                  >
                    <ZoomIn className="h-4 w-4" />
                    <span className="sr-only">Zoom in</span>
                  </Button>
                </>
              )}
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Button type="button" variant="outline" onClick={download} disabled={!file}>
                <Download />
                Download
              </Button>
              <Button type="button" onClick={close}>Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </FileViewerContext.Provider>
  );
}
